import React, { useEffect, useState, useMemo, useRef } from 'react'
import {
  VStack,
  HStack,
  Box,
  Text,
  Collapse,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Spinner
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import { AuthRoleIdEnum } from 'constants/user'
import { IWorkPlanWithRelations, IWorkStation, IWorkplanSearchFilterDto } from 'interfaces/plan'
import { getValidArray } from 'utils/common'
import StationIcon from 'assets/icons/station-icon.svg?react'
import IconAccountTree from 'assets/icons/ic_baseline-account-tree.svg?react'
import IconDetail from 'assets/icons/ic_detail.svg?react'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import { EIconDefaultId } from 'interfaces/iconBuilder'
import SvgIcon from 'components/SvgIcon'
import routes from 'routes'
import { ITheme } from '../../../../interfaces/theme'
import { EStatus } from '../../../../constants/workplan'
import EmptyResult from '../EmptyResult'
import { mapSortTitleToValue } from '../../../../components/Pages/PlansPage/constants'
import PlanSummary from '../../../../components/Pages/PlansPage/components/PlanSummary'

const INITIAL_PAGE_SIZE = 10

const WorkPlanView = () => {
  const { authStore, organizationStore, planStore, iconBuilderStore } = useStores()
  const { userDetail } = authStore
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}
  const navigate = useNavigate()
  const isBasicUser = userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
  const [expandedPlans, setExpandedPlans] = useState<Set<number>>(new Set())
  const [expandedVariants, setExpandedVariants] = useState<Set<number>>(new Set())
  const [selectedPlan, setSelectedPlan] = useState<IWorkPlanWithRelations | undefined>(undefined)
  const { isOpen: isOpenPlan, onOpen: onOpenPlan, onClose: onClosePlan } = useDisclosure()

  const [localWorkplans, setLocalWorkplans] = useState<IWorkPlanWithRelations[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const { isFetchingList, numberOfWorkplans } = planStore

  const defaultIcon = useMemo(() => {
    return iconBuilderStore.getIconById(EIconDefaultId.DOCUMENT_TYPE)
  }, [iconBuilderStore])

  const fetchWorkplans = async (page: number, append: boolean = false) => {
    if (!organization?.id || !userDetail.id) return

    setIsLoadingMore(true)
    try {
      const filterDto: IWorkplanSearchFilterDto = {
        page,
        pageSize: INITIAL_PAGE_SIZE,
        search: '',
        sortBy: 'updatedAt DESC',
        organizationId: organization.id,
        isArchived: false,
      }
      if (isBasicUser) {
        filterDto.status = EStatus.ACTIVE
      }
      const response = await planStore.searchWorkplans(filterDto)
      
      if (append) {
        setLocalWorkplans(prev => {
          const newList = [...prev, ...response.results]
          const totalLoaded = newList.length
          setHasMore(totalLoaded < response.totalCount)
          return newList
        })
      } else {
        setLocalWorkplans(response.results)
        const totalLoaded = response.results.length
        setHasMore(totalLoaded < response.totalCount)
      }
    } catch (error) {
      console.error('Error fetching workplans:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  useEffect(() => {
    if (organization?.id && userDetail.id) {
      setCurrentPage(1)
      setHasMore(true)
      fetchWorkplans(1, false)
    }
  }, [organization?.id, userDetail.id])

  const loadMoreWorkplans = () => {
    if (!isLoadingMore && hasMore) {
      const nextPage = currentPage + 1
      setCurrentPage(nextPage)
      fetchWorkplans(nextPage, true)
    }
  }

  const handlePlanClick = (planId: number) => {
    setExpandedPlans(prev => {
      const newSet = new Set(prev)
      if (newSet.has(planId)) {
        newSet.delete(planId)
      } else {
        newSet.add(planId)
      }
      return newSet
    })
  }

  const handleVariantClick = (planId: number) => {
    setExpandedVariants(prev => {
      const newSet = new Set(prev)
      if (newSet.has(planId)) {
        newSet.delete(planId)
      } else {
        newSet.add(planId)
      }
      return newSet
    })
  }

  const handleStationClick = (planId: number, stationId: number | string | undefined) => {
    if (planId && stationId) {
      const url = `${routes.plans.stationId.value(String(stationId))}?planId=${planId}`
      navigate(url)
    }
  }

  const handleDetailIconClick = (plan: IWorkPlanWithRelations, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedPlan(plan)
    onOpenPlan()
  }

  const getWorkStations = (plan: IWorkPlanWithRelations): IWorkStation[] => {
    return getValidArray(
      (plan.workStationsSummary || []).map(ws => ({
        id: ws._id,
        _id: ws._id,
        name: ws.name,
        steps: new Array(ws.stepCount || 0).fill({}).map((_, idx) => ({ _id: idx + 1 }))
      })) as IWorkStation[]
    )
  }

  return (
    <VStack
      bg="#FFFFFF"
      borderRadius="8px"
      gap={4}
      width="full"
      alignSelf="flex-start"
    >
      <VStack padding={4} spacing={4} alignItems="flex-start" alignSelf="flex-start" width="full">
        <HStack spacing={2} alignItems="center" alignSelf="flex-start">
          <SvgIcon iconName="sidebar-plans" size={24} color="black" />
          <Text fontSize="18px" color="gray.800" fontWeight="600" lineHeight="28px" marginBottom={0}>
            Work Plans
          </Text>
        </HStack>

        {isFetchingList && localWorkplans.length === 0 ? (
          <Box
            width="full"
            height="500px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            marginBottom="16px"
          >
            <Spinner size="md" color="blue.500" />
          </Box>
        ) : getValidArray(localWorkplans).length === 0 ? (
          <Box
            width="full"
            height="500px"
            overflowY="auto"
            marginBottom="16px"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555'
              }
            }}
          >
            <EmptyResult title="No work plans yet." description="You don't have any work plans." />
          </Box>
        ) : (
          <Box
            id="workplan-scroll-container"
            width="full"
            height="500px"
            overflowY="auto"
            marginBottom="16px"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555'
              }
            }}
          >
            <InfiniteScroll
              dataLength={localWorkplans.length}
              next={loadMoreWorkplans}
              hasMore={hasMore}
              loader={
                <Box p={4} textAlign="center" width="full">
                  <Spinner size="md" color="blue.500" />
                </Box>
              }
              scrollableTarget="workplan-scroll-container"
            >
              <VStack spacing={0} alignItems="flex-start" alignSelf="flex-start" width="full">
                {getValidArray(localWorkplans)
                  .filter((plan: IWorkPlanWithRelations) => plan._id && plan._id > 0)
                  .map((plan: IWorkPlanWithRelations) => {
                const hasVariants = Array.isArray(plan.variant) && plan.variant.length > 0
                const planId = plan._id!

                if (hasVariants) {
                  const isVariantGroupExpanded = expandedPlans.has(planId)
                  const variants = getValidArray(plan.variant)

                  return (
                    <Box 
                      key={`group-${planId}`} 
                      width="full"
                      bg={isVariantGroupExpanded ? "gray.100" : "transparent"}
                      borderRadius="8px"
                    >
                      <HStack
                        spacing={4}
                        padding="12px 16px"
                        cursor="pointer"
                        transition="all 0.2s"
                        borderRadius="8px"
                        onClick={() => handlePlanClick(planId)}
                        width="full"
                      >
                        <Box
                          w="40px"
                          h="40px"
                          bg="blue.500"
                          borderRadius="8px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <IconAccountTree width={24} height={24} color="white" />
                        </Box>
                        <Text
                          fontSize="16px"
                          color="gray.700"
                          fontWeight="600"
                          lineHeight="24px"
                          flex={1}
                          marginBottom={0}
                        >
                          {plan.name}
                        </Text>
                        <HStack spacing={2} alignItems="center">
                          <Text fontSize="14px" color="gray.500">
                            {isVariantGroupExpanded ? 'Collapse' : 'Expand'}
                          </Text>
                          {isVariantGroupExpanded ? (
                            <ChevronUpIcon color="#2D3748" boxSize={6} />
                          ) : (
                            <ChevronDownIcon color="#2D3748" boxSize={6} />
                          )}
                        </HStack>
                      </HStack>

                      <Collapse in={isVariantGroupExpanded} animateOpacity>
                        <VStack 
                          spacing={0} 
                          align="stretch" 
                          paddingLeft="8px" 
                          paddingRight="8px"
                        >
                          {variants.map((variant) => {
                            const variantId = variant._id!
                            const isVariantExpanded = expandedVariants.has(variantId)
                            const variantStations = getWorkStations(variant)

                            return (
                              <Box key={`variant-${variantId}`} width="full">
                                <HStack
                                  spacing={3}
                                  padding="12px 16px"
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  borderRadius="8px"
                                  onClick={() => handleVariantClick(variantId)}
                                  width="full"
                                >
                                  <Badge
                                    bg="gray.200"
                                    color="gray.700"
                                    fontSize="12px"
                                    fontWeight="500"
                                    padding="4px 8px"
                                    borderRadius="4px"
                                    flexShrink={0}
                                  >
                                    Variant
                                  </Badge>
                                  <Text
                                    fontSize="16px"
                                    color="gray.700"
                                    fontWeight="600"
                                    lineHeight="24px"
                                    flex={1}
                                    marginBottom={0}
                                  >
                                    {variant.name}
                                  </Text>
                                  <Tooltip
                                    label="Quick view"
                                    height="36px"
                                    fontSize="14px"
                                    padding={2}
                                    background="gray.700"
                                    placement="top"
                                    color="white"
                                    hasArrow
                                    borderRadius="4px"
                                  >
                                    <IconButton
                                      variant="outline"
                                      border="none"
                                      colorScheme="#F7FAFC"
                                      aria-label="Quick view"
                                      icon={<IconDetail />}
                                      size="sm"
                                      onClick={(e) => handleDetailIconClick(variant, e)}
                                    />
                                  </Tooltip>
                                </HStack>

                                <Collapse in={isVariantExpanded} animateOpacity>
                                  {variantStations.length > 0 && (
                                    <VStack spacing={0} align="stretch" paddingLeft="16px" paddingRight="16px" paddingBottom="16px">
                                      {variantStations.map((stationItem, stationIndex) => {
                                        const station = stationItem as IWorkStation
                                        const stationKey = (station.id ?? station._id) as number | string | undefined
                                        const steps = getValidArray(station.steps)

                                        return (
                                          <HStack
                                            key={stationKey || stationIndex}
                                            spacing={4}
                                            padding="8px 12px"
                                            cursor="pointer"
                                            transition="all 0.2s"
                                            borderRadius="6px"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleStationClick(variantId, stationKey)
                                            }}
                                          >
                                            <Box
                                              w="40px"
                                              h="40px"
                                              bg="#02A8B5"
                                              borderRadius="8px"
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              flexShrink={0}
                                            >
                                              <StationIcon width={24} height={24} />
                                            </Box>

                                            <VStack align="start" spacing={1} flex={1}>
                                              <Text fontWeight="500" fontSize="md" color="gray.800">
                                                {station.name || `Station ${stationIndex + 1}`}
                                              </Text>
                                            </VStack>

                                            <Text fontSize="sm" color="gray.500" flexShrink={0}>
                                              {steps.length} step{steps.length !== 1 ? 's' : ''}
                                            </Text>
                                          </HStack>
                                        )
                                      })}
                                    </VStack>
                                  )}
                                </Collapse>
                              </Box>
                            )
                          })}

                          <Box width="full">
                            <HStack
                              spacing={3}
                              padding="12px 16px"
                              cursor="pointer"
                              transition="all 0.2s"
                              borderRadius="8px"
                              onClick={() => handleVariantClick(planId)}
                              width="full"
                            >
                              <Badge
                                bg="gray.200"
                                color="gray.700"
                                fontSize="12px"
                                fontWeight="500"
                                padding="4px 8px"
                                borderRadius="4px"
                                flexShrink={0}
                              >
                                Original
                              </Badge>
                              <Text
                                fontSize="16px"
                                color="gray.700"
                                fontWeight="600"
                                lineHeight="24px"
                                flex={1}
                                marginBottom={0}
                              >
                                {plan.name}
                              </Text>
                              <Tooltip
                                label="Quick view"
                                height="36px"
                                fontSize="14px"
                                padding={2}
                                background="gray.700"
                                placement="top"
                                color="white"
                                hasArrow
                                borderRadius="4px"
                              >
                                <IconButton
                                  variant="outline"
                                  border="none"
                                  colorScheme="#F7FAFC"
                                  aria-label="Quick view"
                                  icon={<IconDetail />}
                                  size="sm"
                                  onClick={(e) => handleDetailIconClick(plan, e)}
                                />
                              </Tooltip>
                            </HStack>

                            <Collapse in={expandedVariants.has(planId)} animateOpacity>
                              {getWorkStations(plan).length > 0 && (
                                <VStack spacing={2} align="stretch" paddingLeft="16px" paddingRight="16px" paddingBottom="16px">
                                  {getWorkStations(plan).map((stationItem, stationIndex) => {
                                    const station = stationItem as IWorkStation
                                    const stationKey = (station.id ?? station._id) as number | string | undefined
                                    const steps = getValidArray(station.steps)

                                    return (
                                      <HStack
                                        key={stationKey || stationIndex}
                                        spacing={4}
                                        padding="8px 12px"
                                        cursor="pointer"
                                        transition="all 0.2s"
                                        borderRadius="6px"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleStationClick(planId, stationKey)
                                        }}
                                      >
                                        <Box
                                          w="40px"
                                          h="40px"
                                          bg="#02A8B5"
                                          borderRadius="8px"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          flexShrink={0}
                                        >
                                          <StationIcon width={24} height={24} />
                                        </Box>

                                        <VStack align="start" spacing={1} flex={1}>
                                          <Text fontWeight="500" fontSize="md" color="gray.800">
                                            {station.name || `Station ${stationIndex + 1}`}
                                          </Text>
                                        </VStack>

                                        <Text fontSize="sm" color="gray.500" flexShrink={0}>
                                          {steps.length} step{steps.length !== 1 ? 's' : ''}
                                        </Text>
                                      </HStack>
                                    )
                                  })}
                                </VStack>
                              )}
                            </Collapse>
                          </Box>
                        </VStack>
                      </Collapse>
                    </Box>
                  )
                }

                const isExpanded = expandedPlans.has(planId)
                const workStations = getWorkStations(plan)

                return (
                  <Box 
                    key={planId} 
                    width="full"
                    bg={isExpanded ? "gray.100" : "transparent"}
                    borderRadius="8px"
                    transition="background-color 0.2s"
                  >
                    <HStack
                      spacing={4}
                      padding="12px 16px"
                      cursor="pointer"
                      transition="all 0.2s"
                      borderRadius="8px"
                      onClick={() => handlePlanClick(planId)}
                      width="full"
                    >
                      {defaultIcon && (
                        <IconBuilder key={`icon-${planId}`} icon={defaultIcon} size={40} isActive />
                      )}
                      <Text
                        fontSize="16px"
                        color="gray.700"
                        fontWeight="600"
                        lineHeight="24px"
                        flex={1}
                        marginBottom={0}
                      >
                        {plan.name}
                      </Text>
                      <Tooltip
                        label="Quick view"
                        height="36px"
                        fontSize="14px"
                        padding={2}
                        background="gray.700"
                        placement="top"
                        color="white"
                        hasArrow
                        borderRadius="4px"
                      >
                        <IconButton
                          variant="outline"
                          border="none"
                          colorScheme="#F7FAFC"
                          aria-label="Quick view"
                          icon={<IconDetail />}
                          size="sm"
                          onClick={(e) => handleDetailIconClick(plan, e)}
                        />
                      </Tooltip>
                    </HStack>

                    <Collapse in={isExpanded} animateOpacity>
                      {workStations.length > 0 && (
                        <VStack spacing={2} align="stretch" paddingLeft="16px" paddingRight="16px" paddingBottom="16px">
                          {workStations.map((stationItem, stationIndex) => {
                            const station = stationItem as IWorkStation
                            const stationKey = (station.id ?? station._id) as number | string | undefined
                            const steps = getValidArray(station.steps)

                            return (
                              <HStack
                                key={stationKey || stationIndex}
                                spacing={4}
                                padding="8px 12px"
                                cursor="pointer"
                                transition="all 0.2s"
                                borderRadius="6px"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleStationClick(planId, stationKey)
                                }}
                              >
                                <Box
                                  w="40px"
                                  h="40px"
                                  bg="#02A8B5"
                                  borderRadius="8px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flexShrink={0}
                                >
                                  <StationIcon width={24} height={24} />
                                </Box>

                                <VStack align="start" spacing={1} flex={1}>
                                  <Text fontWeight="500" fontSize="md" color="gray.800">
                                    {station.name || `Station ${stationIndex + 1}`}
                                  </Text>
                                </VStack>

                                <Text fontSize="sm" color="gray.500" flexShrink={0}>
                                  {steps.length} step{steps.length !== 1 ? 's' : ''}
                                </Text>
                              </HStack>
                            )
                          })}
                        </VStack>
                      )}
                    </Collapse>
                  </Box>
                )
              })}
              </VStack>
            </InfiniteScroll>
          </Box>
        )}
      </VStack>
      <PlanSummary isOpen={isOpenPlan} onClose={onClosePlan} selectedPlan={selectedPlan} />
    </VStack>
  )
}

export default observer(WorkPlanView)

